import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environment/environment';
import type { SaveWizardDTO } from '@libs/dtos/production/production.dto';
import type {
	TelegramWebAppCatalogDTO,
	TelegramWebAppClientDTO,
	TelegramWebAppCupsDTO,
	TelegramWebAppSessionDTO,
	TelegramWebAppSubmitResultDTO,
} from '@libs/dtos/telegram/telegram-webapp.dto';
import { catchError, Observable, throwError } from 'rxjs';

/**
 * Calls of the Telegram Mini App (docs/BOT_TELEGRAM.md §5.5). Every request carries
 * Telegram's signed `initData`, which is the whole identity here: the ERIS session
 * is never used, and the HTTP interceptor lets these URLs through untouched.
 */
@Injectable({ providedIn: 'root' })
export class TelegramWebAppService {
	private readonly baseUrl = `${environment.pathBackend}/telegram/webapp`;

	constructor(private http: HttpClient) {}

	private options(initData: string) {
		return { headers: new HttpHeaders({ 'X-Telegram-Init-Data': initData }) };
	}

	session(initData: string): Observable<TelegramWebAppSessionDTO> {
		return this.http.get<TelegramWebAppSessionDTO>(`${this.baseUrl}/session`, this.options(initData)).pipe(
			catchError((error: HttpErrorResponse) => {
				if (error.status !== 401) return throwError(() => error);
				// Some proxies strip the custom header; the body is the same signature.
				return this.http.post<TelegramWebAppSessionDTO>(
					`${this.baseUrl}/session`,
					{ initData },
					this.options(initData),
				);
			}),
		);
	}

	catalog(initData: string): Observable<TelegramWebAppCatalogDTO> {
		return this.http.get<TelegramWebAppCatalogDTO>(`${this.baseUrl}/catalog`, this.options(initData));
	}

	searchClients(initData: string, search: string): Observable<TelegramWebAppClientDTO[]> {
		return this.http.get<TelegramWebAppClientDTO[]>(`${this.baseUrl}/clients`, {
			...this.options(initData),
			params: { search },
		});
	}

	clientCups(initData: string, clientId: number): Observable<TelegramWebAppCupsDTO[]> {
		return this.http.get<TelegramWebAppCupsDTO[]>(
			`${this.baseUrl}/clients/${clientId}/cups`,
			this.options(initData),
		);
	}

	submit(initData: string, form: SaveWizardDTO): Observable<TelegramWebAppSubmitResultDTO> {
		return this.http.post<TelegramWebAppSubmitResultDTO>(
			`${this.baseUrl}/productions`,
			form,
			this.options(initData),
		);
	}
}
